import React, { useState, useEffect, useRef } from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  TextField,
  CircularProgress,
  Alert,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DomainIcon from "@mui/icons-material/Domain";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useUser } from "../context/UserContext";
import {
  getOrgVerificationStatus,
  startOrgVerification,
  uploadOrgVerificationDocument,
  submitOrgVerification,
} from "../utils/orgVerificationApi";

const DOCUMENT_TYPES = [
  { value: "501c3", label: "501(c)(3) Determination Letter", forSubtype: "nonprofit" },
  { value: "ein_letter", label: "EIN Confirmation Letter", forSubtype: null },
  { value: "business_license", label: "Business License", forSubtype: "business" },
  { value: "certificate_of_incorporation", label: "Certificate of Incorporation", forSubtype: null },
  { value: "articles_of_organization", label: "Articles of Organization", forSubtype: null },
  { value: "other", label: "Other Official Document", forSubtype: null },
];

const ORGANIZATION_SUBTYPES = [
  { value: "nonprofit", label: "Nonprofit / 501(c)(3)" },
  { value: "business", label: "Business" },
  { value: "community", label: "Community Group" },
  { value: "campaign", label: "Political Campaign" },
  { value: "union", label: "Labor Union" },
  { value: "advocacy", label: "Advocacy Organization" },
  { value: "other", label: "Other" },
];

const PRIMARY = "#047857";

const OrganizationVerificationModal = ({ isOpen, onClose }) => {
  const { loadUserContext } = useUser();
  const fileInputRef = useRef(null);

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);

  const [legalName, setLegalName] = useState("");
  const [taxId, setTaxId] = useState("");
  const [organizationSubtype, setOrganizationSubtype] = useState(null);
  const [documentType, setDocumentType] = useState(null);
  const [document, setDocument] = useState(null);

  const [verificationStatus, setVerificationStatus] = useState(null);

  useEffect(() => {
    if (isOpen) {
      loadVerificationStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const loadVerificationStatus = async () => {
    setInitialLoading(true);
    try {
      const status = await getOrgVerificationStatus();
      setVerificationStatus(status);

      if (status.has_verification) {
        const v = status.verification;
        setLegalName(v.legal_name || "");

        if (v.status === "pending" || v.status === "verified") {
          setStep(5);
        } else if (v.status === "rejected") {
          setStep(1);
        } else if (v.document_filename) {
          setStep(4);
        }
      }
    } catch (err) {
      // No existing verification — that's fine, start fresh
    } finally {
      setInitialLoading(false);
    }
  };

  const handleStartVerification = async () => {
    if (!legalName.trim()) {
      setError("Please enter your organization's legal name.");
      return;
    }
    if (!organizationSubtype) {
      setError("Please select your organization type.");
      return;
    }

    setError(null);
    setLoading(true);

    try {
      await startOrgVerification({
        legal_name: legalName.trim(),
        tax_id: taxId.trim() || undefined,
        organization_subtype: organizationSubtype,
      });
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to start verification. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectDocumentType = (type) => {
    setDocumentType(type);
    setStep(3);
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      setError("File must be under 10MB.");
      return;
    }

    setError(null);
    setDocument(file);
    e.target.value = "";
  };

  const handleUploadDocument = async () => {
    if (!document) {
      setError("Please select a document first.");
      return;
    }

    setError(null);
    setLoading(true);

    try {
      await uploadOrgVerificationDocument(documentType, document);
      setStep(4);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to upload document. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitVerification = async () => {
    setError(null);
    setLoading(true);

    try {
      await submitOrgVerification();
      await loadUserContext(true);
      const status = await getOrgVerificationStatus();
      setVerificationStatus(status);
      setStep(5);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to submit verification. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setError(null);
    setDocument(null);
    setLegalName("");
    setTaxId("");
    setOrganizationSubtype(null);
    setDocumentType(null);
    setVerificationStatus(null);
    onClose?.();
  };

  const getDocumentTypeLabel = () => {
    return DOCUMENT_TYPES.find((d) => d.value === documentType)?.label || "Document";
  };

  const renderProgressDots = () => {
    if (step >= 5) return null;
    return (
      <Box sx={{ display: "flex", justifyContent: "center", gap: 1, py: 2 }}>
        {[1, 2, 3, 4].map((s) => (
          <Box
            key={s}
            sx={{
              width: s === step ? 24 : 8,
              height: 8,
              borderRadius: 4,
              backgroundColor: s === step ? PRIMARY : s < step ? PRIMARY : "#d1d5db",
              transition: "all 0.3s ease",
            }}
          />
        ))}
      </Box>
    );
  };

  const renderStep1 = () => (
    <>
      <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a", textAlign: "center", mb: 1 }}>
        Organization Details
      </Typography>
      <Typography variant="body2" sx={{ color: "#374151", textAlign: "center", mb: 3, lineHeight: 1.6 }}>
        Enter your organization's official information for verification.
      </Typography>

      <Box sx={{ mb: 2.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, color: "#0f172a", mb: 1 }}>
          Organization Type *
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {ORGANIZATION_SUBTYPES.map((subtype) => (
            <Box
              key={subtype.value}
              onClick={() => setOrganizationSubtype(subtype.value)}
              sx={{
                px: 1.75,
                py: 1,
                borderRadius: "20px",
                border: "1px solid",
                borderColor: organizationSubtype === subtype.value ? PRIMARY : "#d1d5db",
                backgroundColor: organizationSubtype === subtype.value ? `${PRIMARY}15` : "#fff",
                cursor: "pointer",
                transition: "all 0.2s",
                "&:hover": { borderColor: PRIMARY },
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontSize: "0.8rem",
                  color: organizationSubtype === subtype.value ? PRIMARY : "#0f172a",
                  fontWeight: organizationSubtype === subtype.value ? 600 : 400,
                }}
              >
                {subtype.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <Box sx={{ mb: 2.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, color: "#0f172a", mb: 1 }}>
          Legal Name *
        </Typography>
        <TextField
          fullWidth
          size="small"
          value={legalName}
          onChange={(e) => setLegalName(e.target.value)}
          placeholder="Enter your organization's legal name"
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
              "& fieldset": { borderColor: "#d1d5db" },
              "&:hover fieldset": { borderColor: PRIMARY },
              "&.Mui-focused fieldset": { borderColor: PRIMARY },
            },
          }}
        />
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, color: "#0f172a", mb: 1 }}>
          Tax ID / EIN
        </Typography>
        <TextField
          fullWidth
          size="small"
          value={taxId}
          onChange={(e) => setTaxId(e.target.value)}
          placeholder="e.g., 12-3456789"
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
              "& fieldset": { borderColor: "#d1d5db" },
              "&:hover fieldset": { borderColor: PRIMARY },
              "&.Mui-focused fieldset": { borderColor: PRIMARY },
            },
          }}
        />
        <Typography variant="caption" sx={{ color: "#6b7280", mt: 0.5, display: "block" }}>
          Your Tax ID is encrypted and stored securely.
        </Typography>
      </Box>

      <Button
        fullWidth
        variant="contained"
        onClick={handleStartVerification}
        disabled={loading}
        sx={{
          py: 1.4,
          borderRadius: "12px",
          fontWeight: 600,
          fontSize: "0.95rem",
          textTransform: "none",
          backgroundColor: PRIMARY,
          "&:hover": { backgroundColor: "#065f46" },
        }}
      >
        {loading ? <CircularProgress size={22} sx={{ color: "#fff" }} /> : "Continue"}
      </Button>
    </>
  );

  const renderStep2 = () => (
    <>
      <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a", textAlign: "center", mb: 1 }}>
        Select Document Type
      </Typography>
      <Typography variant="body2" sx={{ color: "#374151", textAlign: "center", mb: 3, lineHeight: 1.6 }}>
        Choose the type of document you'll upload to verify your organization.
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 3 }}>
        {DOCUMENT_TYPES.map((doc) => (
          <Box
            key={doc.value}
            onClick={() => handleSelectDocumentType(doc.value)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              p: 2,
              border: "1px solid #e5e7eb",
              borderRadius: "12px",
              cursor: "pointer",
              transition: "all 0.2s",
              "&:hover": { borderColor: PRIMARY, backgroundColor: `${PRIMARY}08` },
            }}
          >
            <DescriptionOutlinedIcon sx={{ color: PRIMARY, fontSize: 24 }} />
            <Typography variant="body2" sx={{ flex: 1, color: "#0f172a", fontWeight: 500 }}>
              {doc.label}
            </Typography>
            <ChevronRightIcon sx={{ color: "#9ca3af", fontSize: 20 }} />
          </Box>
        ))}
      </Box>

      <Button
        fullWidth
        variant="outlined"
        onClick={() => setStep(1)}
        sx={{
          py: 1.2,
          borderRadius: "12px",
          fontWeight: 500,
          textTransform: "none",
          borderColor: "#d1d5db",
          color: "#0f172a",
          "&:hover": { borderColor: "#9ca3af", backgroundColor: "rgba(0,0,0,0.02)" },
        }}
      >
        Back
      </Button>
    </>
  );

  const renderStep3 = () => (
    <>
      <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a", textAlign: "center", mb: 1 }}>
        Upload Document
      </Typography>
      <Typography variant="body2" sx={{ color: "#374151", textAlign: "center", mb: 3, lineHeight: 1.6 }}>
        Upload your {getDocumentTypeLabel()} for verification.
      </Typography>

      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png"
        style={{ display: "none" }}
        onChange={handleFileChange}
      />

      <Box
        onClick={() => fileInputRef.current?.click()}
        sx={{
          border: "2px dashed",
          borderColor: document ? PRIMARY : "#d1d5db",
          borderRadius: "16px",
          p: 4,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 180,
          cursor: "pointer",
          mb: 3,
          transition: "all 0.2s",
          backgroundColor: document ? `${PRIMARY}05` : "#fafafa",
          "&:hover": { borderColor: PRIMARY, backgroundColor: `${PRIMARY}08` },
        }}
      >
        {document ? (
          <>
            <InsertDriveFileOutlinedIcon sx={{ fontSize: 48, color: PRIMARY, mb: 1 }} />
            <Typography variant="body2" sx={{ fontWeight: 500, color: "#0f172a", textAlign: "center", mb: 0.5 }}>
              {document.name}
            </Typography>
            <Typography variant="caption" sx={{ color: "#6b7280" }}>
              Click to change
            </Typography>
          </>
        ) : (
          <>
            <CloudUploadOutlinedIcon sx={{ fontSize: 48, color: "#9ca3af", mb: 1 }} />
            <Typography variant="body2" sx={{ color: "#0f172a", textAlign: "center", mb: 0.5 }}>
              Click to select a file
            </Typography>
            <Typography variant="caption" sx={{ color: "#6b7280" }}>
              PDF, JPEG, or PNG (max 10MB)
            </Typography>
          </>
        )}
      </Box>

      <Box sx={{ display: "flex", gap: 1.5 }}>
        <Button
          fullWidth
          variant="outlined"
          onClick={() => setStep(2)}
          sx={{
            py: 1.2,
            borderRadius: "12px",
            fontWeight: 500,
            textTransform: "none",
            borderColor: "#d1d5db",
            color: "#0f172a",
            "&:hover": { borderColor: "#9ca3af", backgroundColor: "rgba(0,0,0,0.02)" },
          }}
        >
          Back
        </Button>
        <Button
          fullWidth
          variant="contained"
          onClick={handleUploadDocument}
          disabled={loading || !document}
          sx={{
            py: 1.2,
            borderRadius: "12px",
            fontWeight: 600,
            textTransform: "none",
            backgroundColor: PRIMARY,
            "&:hover": { backgroundColor: "#065f46" },
            "&.Mui-disabled": { opacity: 0.5 },
          }}
        >
          {loading ? <CircularProgress size={22} sx={{ color: "#fff" }} /> : "Upload"}
        </Button>
      </Box>
    </>
  );

  const renderStep4 = () => (
    <>
      <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a", textAlign: "center", mb: 1 }}>
        Review & Submit
      </Typography>
      <Typography variant="body2" sx={{ color: "#374151", textAlign: "center", mb: 3, lineHeight: 1.6 }}>
        Review your information before submitting for verification.
      </Typography>

      <Box sx={{ backgroundColor: "#f8fafc", borderRadius: "12px", p: 2, mb: 2.5 }}>
        {[
          { label: "Legal Name", value: legalName },
          {
            label: "Organization Type",
            value: ORGANIZATION_SUBTYPES.find((s) => s.value === organizationSubtype)?.label || "-",
          },
          { label: "Document Type", value: getDocumentTypeLabel() },
          {
            label: "Document",
            value: document?.name || verificationStatus?.verification?.document_filename || "Uploaded",
          },
        ].map((item, idx) => (
          <Box
            key={idx}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              py: 1.25,
              borderBottom: idx < 3 ? "1px solid #e5e7eb" : "none",
            }}
          >
            <Typography variant="body2" sx={{ color: "#6b7280", fontSize: "0.85rem" }}>
              {item.label}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "#0f172a", fontWeight: 500, fontSize: "0.85rem", textAlign: "right", ml: 2 }}
            >
              {item.value}
            </Typography>
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          display: "flex",
          gap: 1.25,
          backgroundColor: `${PRIMARY}10`,
          p: 1.75,
          borderRadius: "12px",
          mb: 3,
        }}
      >
        <InfoOutlinedIcon sx={{ color: PRIMARY, fontSize: 20, mt: 0.25 }} />
        <Typography variant="body2" sx={{ color: "#0f172a", lineHeight: 1.6, fontSize: "0.8rem" }}>
          Once submitted, our team will review your verification within 24-48 hours.
          You'll be notified by email when approved.
        </Typography>
      </Box>

      <Box sx={{ display: "flex", gap: 1.5 }}>
        <Button
          fullWidth
          variant="outlined"
          onClick={() => setStep(3)}
          sx={{
            py: 1.2,
            borderRadius: "12px",
            fontWeight: 500,
            textTransform: "none",
            borderColor: "#d1d5db",
            color: "#0f172a",
            "&:hover": { borderColor: "#9ca3af", backgroundColor: "rgba(0,0,0,0.02)" },
          }}
        >
          Back
        </Button>
        <Button
          fullWidth
          variant="contained"
          onClick={handleSubmitVerification}
          disabled={loading}
          sx={{
            py: 1.2,
            borderRadius: "12px",
            fontWeight: 600,
            textTransform: "none",
            backgroundColor: PRIMARY,
            "&:hover": { backgroundColor: "#065f46" },
          }}
        >
          {loading ? <CircularProgress size={22} sx={{ color: "#fff" }} /> : "Submit for Review"}
        </Button>
      </Box>
    </>
  );

  const renderStep5 = () => {
    const status = verificationStatus?.verification?.status;
    const isVerified = status === "verified";
    const isPending = status === "pending";
    const isRejected = status === "rejected";

    const statusIcon = isVerified ? (
      <CheckCircleIcon sx={{ fontSize: 64, color: PRIMARY }} />
    ) : isPending ? (
      <AccessTimeIcon sx={{ fontSize: 64, color: "#f59e0b" }} />
    ) : isRejected ? (
      <ErrorOutlineIcon sx={{ fontSize: 64, color: "#ef4444" }} />
    ) : (
      <CheckCircleIcon sx={{ fontSize: 64, color: PRIMARY }} />
    );

    const statusTitle = isVerified
      ? "Verified!"
      : isPending
        ? "Verification Pending"
        : isRejected
          ? "Verification Rejected"
          : "Submitted!";

    const statusMessage = isVerified
      ? "Your organization has been verified. You now have full access to all features."
      : isPending
        ? "Your verification is being reviewed. You'll be notified once approved."
        : isRejected
          ? `Your verification was rejected: ${verificationStatus?.verification?.rejection_reason || "Please try again with different documentation."}`
          : "Your verification has been submitted for review. This usually takes 24-48 hours.";

    return (
      <Box sx={{ textAlign: "center" }}>
        <Box sx={{ mb: 2 }}>{statusIcon}</Box>
        <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a", mb: 1 }}>
          {statusTitle}
        </Typography>
        <Typography variant="body2" sx={{ color: "#374151", lineHeight: 1.6, mb: 3 }}>
          {statusMessage}
        </Typography>

        {isRejected && (
          <Button
            fullWidth
            variant="contained"
            onClick={() => {
              setStep(1);
              setError(null);
              setDocument(null);
            }}
            sx={{
              py: 1.2,
              borderRadius: "12px",
              fontWeight: 600,
              textTransform: "none",
              backgroundColor: PRIMARY,
              mb: 1.5,
              "&:hover": { backgroundColor: "#065f46" },
            }}
          >
            Resubmit Verification
          </Button>
        )}

        <Button
          fullWidth
          variant="text"
          onClick={handleClose}
          sx={{
            py: 1.2,
            fontWeight: 500,
            textTransform: "none",
            color: "#6b7280",
            "&:hover": { backgroundColor: "rgba(0,0,0,0.04)" },
          }}
        >
          Close
        </Button>
      </Box>
    );
  };

  const renderContent = () => {
    if (initialLoading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={32} sx={{ color: PRIMARY }} />
        </Box>
      );
    }

    switch (step) {
      case 1:
        return renderStep1();
      case 2:
        return renderStep2();
      case 3:
        return renderStep3();
      case 4:
        return renderStep4();
      case 5:
        return renderStep5();
      default:
        return renderStep1();
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      aria-labelledby="org-verification-title"
      sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <Box
        sx={{
          width: 520,
          maxHeight: "90vh",
          bgcolor: "#ffffff",
          borderRadius: "14px",
          boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            backgroundColor: PRIMARY,
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 3,
            py: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                display: "grid",
                placeItems: "center",
                backgroundColor: "rgba(255,255,255,0.15)",
              }}
            >
              <DomainIcon fontSize="small" />
            </Box>
            <Typography id="org-verification-title" variant="h6" sx={{ fontWeight: 700 }}>
              Organization Verification
            </Typography>
          </Box>
          <IconButton onClick={handleClose} sx={{ color: "#fff" }} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {renderProgressDots()}

        {/* Body */}
        <Box sx={{ px: 4, py: 3, overflow: "auto", flex: 1 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: "8px" }}>
              {error}
            </Alert>
          )}
          {renderContent()}
        </Box>
      </Box>
    </Modal>
  );
};

export default OrganizationVerificationModal;
